import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchCoinMarketData } from '@/lib/api'
import type { MarketRecord, CoinMarketData, ExchangeMarketData } from '@/types'

interface UseCoinMarketDataParams {
  symbols: string[]
  period: string
  includeSpot: boolean
  includeFutures: boolean
  exchanges: string[]
  enabled: boolean
}

interface UseCoinMarketDataResult {
  data: CoinMarketData[]
  records: MarketRecord[]
  isLoading: boolean
  error: string | null
  lastUpdated: Date | null
}

// Generate a unique session ID for this hook instance
const generateSessionId = () => `user-${Math.random().toString(36).slice(2)}`
const EMPTY_RECORDS: MarketRecord[] = []

// A total is only meaningful when every included market reported its volume.
// Missing data must not silently turn into zero or an understated total.
function sumMarketVolume(
  records: MarketRecord[],
  field: 'volume_current' | 'volume_prev',
): number | null {
  if (records.length === 0) return null
  let total = 0
  for (const record of records) {
    const volume = record[field]
    if (typeof volume !== 'number' || !Number.isFinite(volume) || volume < 0) return null
    total += volume
  }
  return total
}

export function useCoinMarketData({
  symbols,
  period,
  includeSpot,
  includeFutures,
  exchanges,
  enabled,
}: UseCoinMarketDataParams): UseCoinMarketDataResult {
  const [sessionId] = useState(generateSessionId)
  const requestedSymbols = useMemo(
    () => Array.from(new Set(symbols.map((symbol) => symbol.trim().toUpperCase()).filter(Boolean))).sort(),
    [symbols],
  )
  const requestedExchanges = useMemo(
    () => Array.from(new Set(exchanges.map((exchange) => exchange.trim().toLowerCase()).filter(Boolean))).sort(),
    [exchanges],
  )
  const canFetch = enabled && requestedExchanges.length > 0 && (includeSpot || includeFutures)
  const query = useQuery({
    queryKey: ['coin-market-data', sessionId, requestedSymbols, period, includeSpot, includeFutures, requestedExchanges],
    queryFn: ({ signal }) => fetchCoinMarketData(
      sessionId, requestedSymbols, period, includeSpot, includeFutures, requestedExchanges, signal,
    ),
    enabled: canFetch,
    refetchInterval: 5_000,
    staleTime: 4_000,
    retry: false,
  })
  const records = canFetch ? query.data?.items ?? EMPTY_RECORDS : EMPTY_RECORDS
  const lastUpdated = useMemo(
    () => canFetch && query.dataUpdatedAt ? new Date(query.dataUpdatedAt) : null,
    [canFetch, query.dataUpdatedAt],
  )

  // Aggregate records into coin-level data
  const data = useMemo(() => {
    if (records.length === 0) return []

    // Group records by symbol.
    // Records come from the backend, so guard against missing/null fields here:
    // this runs during render and an unguarded `.toUpperCase()` on a bad record
    // would throw and blank the entire page.
    const symbolMap = new Map<string, MarketRecord[]>()
    
    for (const record of records) {
      if (!record || typeof record.symbol !== 'string' || !record.symbol) continue
      const symbol = record.symbol.toUpperCase()
      if (!symbolMap.has(symbol)) {
        symbolMap.set(symbol, [])
      }
      symbolMap.get(symbol)!.push(record)
    }

    // Convert to CoinMarketData
    const coinData: CoinMarketData[] = []

    for (const [symbol, symbolRecords] of symbolMap) {
      // Extract base symbol (e.g., BTC from BTC/USDT)
      const baseSymbol = symbol.includes('/') ? symbol.split('/')[0] : symbol

      // Group by exchange
      const exchangeMap = new Map<string, ExchangeMarketData>()

      for (const record of symbolRecords) {
        const exchange = (record.exchange || 'unknown').toLowerCase()
        
        if (!exchangeMap.has(exchange)) {
          exchangeMap.set(exchange, { exchange })
        }

        const exchangeData = exchangeMap.get(exchange)!
        
        if (record.type === 'SPOT') {
          exchangeData.spot = record
        } else if (record.type === 'FUTURES') {
          exchangeData.futures = record
        }
      }

      const exchanges = Array.from(exchangeMap.values())

      // Calculate totals and averages
      const markets = exchanges.flatMap(({ spot, futures }) =>
        [spot, futures].filter((record): record is MarketRecord => record !== undefined),
      )
      const totalVolumeCurrent = sumMarketVolume(markets, 'volume_current')
      const totalVolumePrev = sumMarketVolume(markets, 'volume_prev')
      let totalOICurrent = 0
      let totalOIPrev = 0
      let priceSum = 0
      let priceCount = 0
      let spotCount = 0
      let futuresCount = 0

      for (const exchangeData of exchanges) {
        if (exchangeData.spot) {
          spotCount++
          if (exchangeData.spot.price) {
            priceSum += exchangeData.spot.price
            priceCount++
          }
        }
        if (exchangeData.futures) {
          futuresCount++
          if (exchangeData.futures.open_interest?.current) totalOICurrent += exchangeData.futures.open_interest.current
          if (exchangeData.futures.open_interest?.prev) totalOIPrev += exchangeData.futures.open_interest.prev
          if (exchangeData.futures.price) {
            priceSum += exchangeData.futures.price
            priceCount++
          }
        }
      }

      const volumeChangePercent = totalVolumeCurrent !== null && totalVolumePrev !== null && totalVolumePrev > 0
        ? ((totalVolumeCurrent - totalVolumePrev) / totalVolumePrev) * 100 
        : null

      const oiChangePercent = totalOIPrev > 0 
        ? ((totalOICurrent - totalOIPrev) / totalOIPrev) * 100 
        : 0

      const avgPrice = priceCount > 0 ? priceSum / priceCount : null

      coinData.push({
        symbol,
        baseSymbol,
        exchanges,
        totalVolumeCurrent,
        totalVolumePrev,
        volumeChangePercent,
        totalOICurrent,
        totalOIPrev,
        oiChangePercent,
        avgPrice,
        exchangeCount: exchanges.length,
        spotCount,
        futuresCount,
      })
    }

    // Sort by symbol
    coinData.sort((a, b) => a.symbol.localeCompare(b.symbol))

    return coinData
  }, [records])

  return {
    data,
    records,
    isLoading: canFetch && query.isFetching,
    error: canFetch && query.error ? query.error.message : null,
    lastUpdated,
  }
}
